<template>
  <div
    class="input w-full"
    :class="{
      'input--error': hasError,
      'input--focus': isInputFocused || modelValue,
      'input--simple': isSimpleDesign,
      'input--disabled': isDisabled,
      'mb-24': !hideMarginBottom
    }"
    :style="{ borderColor: borderColor }"
  >
    <template v-if="leftIconClass">
      <div
        :class="[leftIconClass]"
        class="input__left-icon pl-16"
        @click="$emit('on-left-click')"
      ></div>
    </template>
    <div class="input__field-wrapper">
      <input
        :id="id"
        class="input__field px-16"
        :class="{ 'pt-16': label }"
        :value="modelValue"
        :disabled="isDisabled"
        :autocomplete="autocomplete"
        :type="innerType"
        :required="required"
        :placeholder="placeholder"
        @input="$emit('update:modelValue', $event.target.value)"
        @focus="toggleInputFocus(true)"
        @blur="toggleInputFocus(false)"
      />
      <label v-if="label" class="input__label" :for="id">
        {{ label }}
      </label>
      <template v-if="fixedAppendText">
        <span class="flex items-center pr-16">
          {{ fixedAppendText }}
        </span>
      </template>
    </div>
    <template v-if="rightIconClass">
      <template v-if="hasRightResetButton">
        <div
          v-if="modelValue?.length"
          :class="[rightIconClass]"
          class="input__right-icon pr-16"
          @click="$emit('on-right-click')"
        ></div>
      </template>
      <div
        v-else
        :class="[rightIconClass]"
        class="input__right-icon pr-16"
        @click="$emit('on-right-click')"
      ></div>
    </template>
    <template v-if="type === 'password'">
      <div
        :class="[passwordIconClass]"
        class="input__right-icon pr-16"
        @click="togglePasswordType"
      ></div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'InputField',
  props: {
    modelValue: {
      type: [String, Number, null],
      required: true
    },
    type: {
      type: String,
      default: 'text'
    },
    required: {
      type: Boolean,
      default: true
    },
    autocomplete: {
      type: String,
      default: 'off'
    },
    id: {
      type: String,
      default: ''
    },
    hideMarginBottom: {
      type: Boolean,
      default: false
    },
    leftIconClass: {
      type: String,
      default: ''
    },
    rightIconClass: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    hasError: {
      type: Boolean,
      default: false
    },
    hasRightResetButton: {
      type: Boolean,
      default: false
    },
    fixedAppendText: {
      type: String,
      default: ''
    },
    borderColor: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ' '
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    isSimpleDesign: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'on-left-click', 'on-right-click'],
  data() {
    return {
      innerType: '',
      isInputFocused: false
    }
  },
  computed: {
    passwordIconClass() {
      return this.innerType === 'password' ? 'icon-eye-open' : 'icon-eye-close'
    }
  },
  mounted() {
    this.innerType = this.type
  },
  methods: {
    togglePasswordType() {
      this.innerType = this.innerType === 'password' ? 'text' : 'password'
    },
    toggleInputFocus(state) {
      this.isInputFocused = state
    }
  }
}
</script>

<style lang="scss">
.input {
  width: 100%;
  border-radius: rem(16);
  border: 2px solid var(--color-primary);
  position: relative;
  display: flex;
  align-items: center;
  background-color: var(--color-secondary);

  &--simple {
    border-radius: var(--grid-8);

    .input__field-wrapper {
      height: var(--grid-32);
    }
  }

  &--disabled {
    background-color: var(--color-gray-2);
    border-color: var(--color-brand-purple-soft) !important;
    color: var(--color-gray-3);
  }

  &--focus {
    border-color: var(--color-brand-purple);
  }

  &__field-wrapper {
    position: relative;
    width: 100%;
    height: rem(52);
    display: flex;
  }

  &__label {
    position: absolute;
    left: rem(16);
    top: 50%;
    transform: translateY(-50%);
    color: var(--color-gray-4);
    transition: all 0.2s ease;
    pointer-events: none;
    font-size: rem(16);
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 90%;
  }

  &__field {
    height: 100%;
    width: 100%;
    font-size: rem(16);
    line-height: rem(24);
    letter-spacing: rem(0.75);

    &:disabled ~ .input__label,
    &:focus ~ .input__label,
    &:not(input:placeholder-shown) ~ .input__label {
      font-size: rem(13);
      transform: translateY(-140%);
    }

    &[disabled] {
      color: var(--color-gray-3);
      cursor: not-allowed;

      &::-webkit-input-placeholder {
        color: var(--color-gray-3);
      }
    }
  }

  &__left-icon {
    text-align: center;

    &--hover {
      &:hover {
        opacity: 0.5;
      }
    }
  }

  &__right-icon {
    cursor: pointer;

    &--hover {
      &:hover {
        opacity: 0.5;
      }
    }
  }

  &--error {
    border-color: var(--color-brand-red) !important;
  }
}
</style>
